import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip
} from "recharts";

/**
 * Operational Complexity Radar Chart
 * 
 * Displays 5 dimensions of operational complexity:
 * - Tool Sprawl
 * - Manual Processes
 * - Data Fragmentation
 * - Team Scale
 * - System Brittleness
 */
const ComplexityChart = ({ data }) => {
  if (!data || !data.dimensions) return null;

  const chartData = data.dimensions.map(d => ({
    dimension: d.name,
    value: d.value,
    fullMark: 100
  }));

  return (
    <div className="w-full" data-testid="complexity-chart">
      <div className="mb-4">
        <h3 className="font-heading text-lg font-semibold text-white print:text-slate-900">
          {data.title || "Operational Complexity Profile"}
        </h3>
        <p className="text-slate-400 print:text-slate-600 text-sm">{data.subtitle}</p>
      </div>
      
      {/* Screen version with ResponsiveContainer */}
      <div className="h-[300px] w-full print:hidden">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
            <PolarGrid stroke="#334155" />
            <PolarAngleAxis 
              dataKey="dimension" 
              tick={{ fill: '#94a3b8', fontSize: 11 }}
              tickLine={false}
            />
            <PolarRadiusAxis 
              angle={30} 
              domain={[0, 100]} 
              tick={{ fill: '#64748b', fontSize: 10 }}
              axisLine={false}
            />
            <Radar
              name="Complexity"
              dataKey="value"
              stroke="#d97706"
              fill="#d97706"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#fff'
              }}
              formatter={(value) => [`${value}/100`, 'Score']}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Print version with fixed dimensions */}
      <div className="hidden print:block w-full" style={{ height: '280px' }}>
        <RadarChart 
          width={400} 
          height={280} 
          cx="50%" 
          cy="50%" 
          outerRadius="65%" 
          data={chartData}
          style={{ margin: '0 auto' }}
        >
          <PolarGrid stroke="#94a3b8" />
          <PolarAngleAxis 
            dataKey="dimension" 
            tick={{ fill: '#374151', fontSize: 10 }}
            tickLine={false}
          />
          <PolarRadiusAxis 
            angle={30} 
            domain={[0, 100]} 
            tick={{ fill: '#6b7280', fontSize: 9 }}
            axisLine={false}
          />
          <Radar
            name="Complexity"
            dataKey="value"
            stroke="#b45309"
            fill="#d97706"
            fillOpacity={0.4}
            strokeWidth={2}
          />
        </RadarChart>
      </div>
      
      {/* Overall Score Badge */}
      <div className="flex items-center justify-center mt-4">
        <div className="bg-navy-950/50 print:bg-slate-100 px-4 py-2 rounded-lg border border-slate-700 print:border-slate-300">
          <span className="text-slate-400 print:text-slate-600 text-sm mr-2">Overall Complexity:</span>
          <span className="text-primary print:text-amber-700 font-heading font-bold text-xl">{data.overall_score}</span>
          <span className="text-slate-500 print:text-slate-600 text-sm">/100</span>
        </div>
      </div>

      {/* Print-friendly data table (backup) */}
      <div className="hidden print:block mt-4 p-3 border border-slate-300 rounded bg-slate-50">
        <p className="text-slate-700 font-medium text-xs mb-2">Complexity Dimensions:</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {data.dimensions.map((d, idx) => (
            <div key={idx} className="flex justify-between items-center text-xs">
              <span className="text-slate-600">{d.name}:</span>
              <span className="text-slate-900 font-medium">{d.value}/100</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplexityChart;
